import { type FC, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { EventType, trackEvent } from "@/shared/utils/core";
import { secondsSince } from "@/shared/utils/funnelAnalytics";
import { StoryCarousel } from "@/pages/onboarding/components/StoryCarousel";
import logoFull from "@/shared/assets/logo/logo-full.svg";

import { CLIENT_INVITE_STORIES } from "../clientInviteStories";

interface ClientInviteLandingProps {
  clientEmail: string;
  lawyerName: string;
  firmName: string | null;
  avatarUrl: string | null;
  isExistingUser: boolean;
  token: string;
}

const FEATURES = [
  "A private workspace, ready when you are",
  "One estate record you build together, so nothing gets lost in emails and calls",
  "Elayne's help finding assets and handling 25+ admin tasks",
];

export const ClientInviteLanding: FC<ClientInviteLandingProps> = ({
  lawyerName,
  firmName,
  avatarUrl,
  isExistingUser,
  token,
}) => {
  const navigate = useNavigate();
  const enteredAtRef = useRef(Date.now());

  const handleAccept = () => {
    trackEvent(EventType.ONBOARDING_STEP_COMPLETED, {
      flow: "attorney_invite",
      step_id: "client-invite-intro",
      step_number: 1,
      time_on_step_seconds: secondsSince(enteredAtRef.current),
      next_step_id: "client-invite-signup",
    });
    navigate(`/client-invite/signup?token=${token}${isExistingUser ? "&existing=true" : ""}`);
  };

  return (
    <div className="flex h-screen bg-subtle">
      <div className="relative flex flex-1 flex-col items-center justify-center overflow-y-auto p-4 sm:p-10">
        <img src={logoFull} alt="Elayne" className="absolute left-4 top-4 h-8 sm:left-8 sm:top-8" />

        <div className="flex w-full max-w-[500px] flex-col items-center gap-6">
          <div className="flex flex-col gap-3 text-center">
            <h2>Your attorney, {lawyerName}, set up a private workspace for you</h2>
            <p className="text-sm text-secondary">
              Everything you need to settle the estate, in one place
            </p>
          </div>

          <div className="w-full overflow-hidden rounded-lg border border-primary bg-white">
            <div className="flex flex-col gap-4 p-6">
              <div className="flex items-center gap-4">
                {avatarUrl && (
                  <img
                    src={avatarUrl}
                    alt={lawyerName}
                    className="h-12 w-12 shrink-0 rounded-full object-cover"
                  />
                )}
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="text-sm font-medium">{lawyerName}</span>
                  {firmName && <span className="text-sm text-secondary">{firmName}</span>}
                </div>
              </div>

              <div className="h-px w-full bg-stone-200" />

              <div className="flex flex-col gap-4">
                <span className="text-sm font-medium">What they&apos;ve arranged for you</span>
                <div className="flex flex-col gap-2">
                  {FEATURES.map((feature) => (
                    <div key={feature} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
                      <span className="text-sm text-secondary">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <Button variant="brand" rounded onClick={handleAccept} className="w-full">
            Accept invitation
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <div className="hidden p-4 pl-0 lg:flex lg:w-[500px]">
        <StoryCarousel stories={CLIENT_INVITE_STORIES} className="flex-1" />
      </div>
    </div>
  );
};
